import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PercentIcon from "@mui/icons-material/Percent";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Swal from "sweetalert2";
import { LoaderScreen } from "../../../components/LoaderScreen";
import { FormatMoney } from "../../../../Helpers/FormatNumber";
import request from "../../../../Helpers/API";
import { CondonacionInteresDetalleModal } from "./CondonacionInteresDetalleModal";
import { FiltroCondonacionesGlobal } from "./FiltroCondonacionesGlobal";
import { useBox } from "../../ReporteCobranza/Helper/useBox";
import FiltroMultiple from "../../../Postventa/Dashboard/Modales/FiltroMultiple";

// Fecha del backend viene como YYYY-MM-DD... — se muestra como dd/mm/yyyy.
const formatFecha = (fecha) => {
    if (!fecha) return "—";
    const [anio, mes, dia] = fecha.slice(0, 10).split("-");
    return `${dia}/${mes}/${anio}`;
};

// Historial de condonaciones ya registradas para el cliente —
// CondonacionIntereses/ObtenerCondonacionesCliente. Solo consulta; editar/cancelar un registro
// existente no tiene endpoint todavía.
export const CondonacionInteresListado = ({ tienePendiente, onNuevaCondonacion, onAprobarCondonacion }) => {
    const history = useHistory();
    const { state } = useLocation();
    const esGlobal = !state?.idcliente || Number(state.idcliente) === 0;
    const [condonaciones, setCondonaciones] = useState([]);
    const [loader, setLoader] = useState({ mostrar: false, alerta: false, error: false, mensaje: "" });
    const [folioDetalle, setFolioDetalle] = useState(null);
    const [boxstate, setBoxState] = useBox();

    // Filtro de columna (Cliente/Autoriza) del reporte global — filtra client-side sobre los
    // registros ya cargados, mismo patrón (FiltroMultiple + prioridad) que otras tablas del sistema.
    const [filtros, setFiltros] = useState({ cliente: [], autoriza: [] });
    const [filtrosTemp, setFiltrosTemp] = useState({ cliente: [], autoriza: [] });
    const [prioridadFiltros, setPrioridadFiltros] = useState([]);
    const [mostrarFiltro, setMostrarFiltro] = useState({ cliente: {}, autoriza: {} });

    const actualizarPrioridad = (filtro) => {
        setPrioridadFiltros((prev) => {
            const index = prev.indexOf(filtro);
            return index === -1 ? [...prev, filtro] : prev.slice(0, index + 1);
        });
    };

    const condonacionesFiltradas = useMemo(() => {
        if (!esGlobal) return condonaciones;
        let data = [...condonaciones];
        if (filtros.cliente.length) data = data.filter((i) => filtros.cliente.includes(i.razon_social));
        if (filtros.autoriza.length) data = data.filter((i) => filtros.autoriza.includes(i.autoriza));
        return data;
    }, [esGlobal, condonaciones, filtros]);

    const opcionesCliente = useMemo(() => {
        const base = filtros.autoriza.length ? condonaciones.filter((i) => filtros.autoriza.includes(i.autoriza)) : condonaciones;
        return [...new Set(base.map((i) => i.razon_social).filter(Boolean))].map((v) => ({ value: v, label: v }));
    }, [condonaciones, filtros.autoriza]);

    const opcionesAutoriza = useMemo(() => {
        const base = filtros.cliente.length ? condonaciones.filter((i) => filtros.cliente.includes(i.razon_social)) : condonaciones;
        return [...new Set(base.map((i) => i.autoriza).filter(Boolean))].map((v) => ({ value: v, label: v }));
    }, [condonaciones, filtros.cliente]);

    const totalesGlobal = useMemo(() => {
        if (!esGlobal) return null;
        return condonacionesFiltradas.reduce(
            (acc, item) => {
                const totalInteres = (item.inormal_saldo || 0) + (item.imoratorio_saldo || 0);
                const totalCondonado = (item.inormal_descuento || 0) + (item.imoratorio_descuento || 0);
                acc.totalInteres += totalInteres;
                acc.totalCondonado += totalCondonado;
                acc.interesPagado += totalInteres - totalCondonado;
                return acc;
            },
            { totalInteres: 0, totalCondonado: 0, interesPagado: 0 }
        );
    }, [esGlobal, condonacionesFiltradas]);

    const handleNuevaCondonacion = () => {
        if (tienePendiente) {
            Swal.fire(
                "CONDONACIÓN DE INTERÉS",
                "No puedes realizar otra condonación a este cliente hasta que se autorice o rechace la que está pendiente.",
                "warning"
            );
            return;
        }
        onNuevaCondonacion();
    };

    // Listado global (todos los clientes) — CondonacionIntereses/ObtenerCondonacionesReporte.
    const onExecuteGlobal = (ejercicio, periodo, sucursal, adr) => {
        const idsSucursal = (sucursal || []).map((s) => s.idsucursal).join(",") || "0";
        const idsAdr = (adr || []).map((a) => a.idadr).join(",") || "0";
        setLoader((prev) => ({ ...prev, mostrar: true }));
        return request
            .post(`CondonacionIntereses/ObtenerCondonacionesReporte?ejercicio=${ejercicio}&periodo=${periodo}&adr=${idsAdr}&sucursal=${idsSucursal}`, {})
            .then((result) => {
                setCondonaciones(Array.isArray(result) ? result : []);
                setLoader((prev) => ({ ...prev, mostrar: false }));
            })
            .catch(() => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                Swal.fire("CONDONACIÓN DE INTERÉS", "No se pudo cargar el reporte de condonaciones.", "error");
            });
    };

    useEffect(() => {
        if (esGlobal) {
            const ejercicioInicial = boxstate?.ejercicio || new Date().getFullYear();
            const periodoInicial = boxstate?.periodo || new Date().getMonth() + 1;
            onExecuteGlobal(ejercicioInicial, periodoInicial, [], []);
            return;
        }
        setLoader((prev) => ({ ...prev, mostrar: true }));
        request
            .post(`CondonacionIntereses/ObtenerCondonacionesCliente?idcliente=${state.idcliente}`, {})
            .then((result) => {
                setCondonaciones(Array.isArray(result) ? result : []);
                setLoader((prev) => ({ ...prev, mostrar: false }));
            })
            .catch(() => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                Swal.fire("CONDONACIÓN DE INTERÉS", "No se pudo cargar el historial del cliente.", "error");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="crm-main-screen">
            <LoaderScreen loader={loader} />
            <div className="crm-body">
                <div className="crm-page">
                    <div className="crm-module">
                        <div className="crm-module-head">
                            <div className="crm-head-grid">
                                <div className="crm-head-left">
                                    <button className="crm-btn-regresar" onClick={() => history.goBack()}>
                                        <ArrowBackIcon /> Regresar
                                    </button>
                                    {esGlobal && (
                                        <div className="cia-filtro-global-wrap">
                                            <FiltroCondonacionesGlobal
                                                onExecute={onExecuteGlobal}
                                                boxState={boxstate}
                                                onBoxChange={setBoxState}
                                                sucursales={state?.permisos}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="crm-head-center">
                                    <h1 className="crm-module-title">{esGlobal ? "CONDONACIONES DE INTERÉS" : state.razonsocial}</h1>
                                    <p className="crm-module-subtitle">
                                        {esGlobal ? "TODOS LOS CLIENTES" : "CONDONACIONES DE INTERÉS"}
                                    </p>
                                </div>
                                <div className="crm-head-right">
                                    {esGlobal ? (
                                        <span className="cia-contador-global">{condonacionesFiltradas.length} registros</span>
                                    ) : (
                                        <button className="crm-btn-action" onClick={handleNuevaCondonacion}>
                                            <AddCircleOutlineIcon /> <span className="crm-btn-label">Nueva condonación</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="crm-head-divider">
                                <div className="crm-head-divider-dot" />
                            </div>
                        </div>

                        <div className={`mjr-wrapper cia-wrapper-listado ${esGlobal ? "cia-wrapper-listado-global" : ""}`}>
                            <div className="cdi-panel">
                                {!esGlobal && (
                                    <div className="cdi-panel-header">
                                        <div className="cdi-panel-header-icono"><PercentIcon /></div>
                                        <h3 className="cdi-panel-titulo">Historial de condonaciones</h3>
                                        <span className="cdi-panel-badge">{condonaciones.length} registros</span>
                                    </div>
                                )}

                                <div className="cdi-tabla-wrap scroll">
                                    <table className={`cdi-tabla cdi-tabla-condonaciones ${esGlobal ? "cdi-tabla-condonaciones-global" : ""}`} cellPadding={0} cellSpacing={0}>
                                        <thead>
                                            <tr>
                                                <th><div>{esGlobal ? "" : "Ver"}</div></th>
                                                {esGlobal ? (
                                                    <>
                                                        <th>
                                                            <div className="borde-izq">
                                                                <FiltroMultiple
                                                                    label="Cliente"
                                                                    field="cliente"
                                                                    options={opcionesCliente}
                                                                    filtros={filtros}
                                                                    setFiltros={setFiltros}
                                                                    filtrosTemp={filtrosTemp}
                                                                    setFiltrosTemp={setFiltrosTemp}
                                                                    mostrarFiltro={mostrarFiltro}
                                                                    setMostrarFiltro={setMostrarFiltro}
                                                                    actualizarPrioridad={actualizarPrioridad}
                                                                    setPrioridadFiltros={setPrioridadFiltros}
                                                                />
                                                            </div>
                                                        </th>
                                                        <th><div className="borde-izq">Fecha</div></th>
                                                        <th><div className="borde-izq">Total Interés</div></th>
                                                        <th><div className="borde-izq">Total condonado</div></th>
                                                        <th><div className="borde-izq">%</div></th>
                                                        <th><div className="borde-izq">Interés Pagado</div></th>
                                                        <th>
                                                            <div className="borde-izq">
                                                                <FiltroMultiple
                                                                    label="Autoriza"
                                                                    field="autoriza"
                                                                    options={opcionesAutoriza}
                                                                    filtros={filtros}
                                                                    setFiltros={setFiltros}
                                                                    filtrosTemp={filtrosTemp}
                                                                    setFiltrosTemp={setFiltrosTemp}
                                                                    mostrarFiltro={mostrarFiltro}
                                                                    setMostrarFiltro={setMostrarFiltro}
                                                                    actualizarPrioridad={actualizarPrioridad}
                                                                    setPrioridadFiltros={setPrioridadFiltros}
                                                                />
                                                            </div>
                                                        </th>
                                                        <th><div className="borde-izq">Comentarios</div></th>
                                                    </>
                                                ) : (
                                                    <>
                                                        <th><div className="borde-izq">Folio</div></th>
                                                        <th><div className="borde-izq">Fecha</div></th>
                                                        <th><div className="borde-izq">Saldo</div></th>
                                                        <th><div className="borde-izq">Total condonado</div></th>
                                                        <th><div className="borde-izq">Autoriza</div></th>
                                                        <th><div className="borde-izq">Comentarios</div></th>
                                                        <th><div className="borde-izq">Creador</div></th>
                                                        <th><div className="borde-izq">Estatus</div></th>
                                                    </>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {condonacionesFiltradas.map((item) => {
                                                const pendiente = item.estatus === "Autorización pendiente";
                                                const claseEstatus =
                                                    item.estatus === "Autorizada"
                                                        ? "cdi-badge-on"
                                                        : item.estatus === "Rechazada"
                                                            ? "cdi-badge-rechazada"
                                                            : "cdi-badge-off";
                                                const claseFilaEstatus =
                                                    item.estatus === "Autorizada"
                                                        ? "cdi-fila-estatus-on"
                                                        : item.estatus === "Rechazada"
                                                            ? "cdi-fila-estatus-rechazada"
                                                            : "cdi-fila-estatus-off";

                                                if (esGlobal) {
                                                    const totalInteres = (item.inormal_saldo || 0) + (item.imoratorio_saldo || 0);
                                                    const totalCondonado = (item.inormal_descuento || 0) + (item.imoratorio_descuento || 0);
                                                    const interesPagado = totalInteres - totalCondonado;
                                                    const pctCondonado = totalInteres ? (totalCondonado / totalInteres) * 100 : 0;
                                                    return (
                                                        <tr key={item.folio} className={claseFilaEstatus}>
                                                            <td className="cdi-tabla-nowrap">
                                                                <button
                                                                    type="button"
                                                                    className="cdi-btn-icono"
                                                                    onClick={() => setFolioDetalle(item.folio)}
                                                                >
                                                                    <VisibilityIcon />
                                                                </button>
                                                            </td>
                                                            <td className="cdi-tabla-nowrap cdi-td-izq cdi-td-cliente">{item.razon_social || "—"}</td>
                                                            <td className="cdi-tabla-nowrap cdi-td-fecha">{formatFecha(item.createdate)}</td>
                                                            <td className="cdi-tabla-nowrap cdi-tabla-pct cdi-td-importe">{FormatMoney(totalInteres)}</td>
                                                            <td className="cdi-tabla-nowrap cdi-tabla-pct cdi-td-importe">{FormatMoney(totalCondonado)}</td>
                                                            <td className="cdi-tabla-nowrap cdi-tabla-pct cdi-td-pct">{pctCondonado.toFixed(1)}%</td>
                                                            <td className="cdi-tabla-nowrap cdi-tabla-pct cdi-td-importe">{FormatMoney(interesPagado)}</td>
                                                            <td className="cdi-tabla-nowrap cdi-td-izq cdi-td-autoriza">{item.autoriza || "—"}</td>
                                                            <td className="cdi-tabla-comentarios">{item.comentarios || "—"}</td>
                                                        </tr>
                                                    );
                                                }

                                                return (
                                                    <tr
                                                        key={item.folio}
                                                        className={`${claseFilaEstatus} ${pendiente ? "cdi-tabla-fila-clicable" : ""}`}
                                                        onClick={() => pendiente && onAprobarCondonacion(item.folio)}
                                                    >
                                                        <td className="cdi-tabla-nowrap">
                                                            <button
                                                                type="button"
                                                                className="cdi-btn-icono"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setFolioDetalle(item.folio);
                                                                }}
                                                            >
                                                                <VisibilityIcon />
                                                            </button>
                                                        </td>
                                                        <td className="cdi-tabla-nowrap cdi-tabla-usuario">
                                                            {item.estatus === "Autorizada" ? item.folio : "—"}
                                                        </td>
                                                        <td className="cdi-tabla-nowrap">{formatFecha(item.createdate)}</td>
                                                        <td className="cdi-tabla-nowrap cdi-tabla-pct">{FormatMoney(item.saldo)}</td>
                                                        <td className="cdi-tabla-nowrap">
                                                            {FormatMoney((item.inormal_descuento || 0) + (item.imoratorio_descuento || 0))}
                                                        </td>
                                                        <td className="cdi-tabla-nowrap">{item.autoriza || "—"}</td>
                                                        <td className="cdi-tabla-comentarios">{item.comentarios || "—"}</td>
                                                        <td className="cdi-tabla-nowrap">{item.creador}</td>
                                                        <td className="cdi-tabla-nowrap">
                                                            <span className={`cdi-badge ${claseEstatus}`}>
                                                                {item.estatus}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {condonacionesFiltradas.length === 0 && (
                                                <tr>
                                                    <td colSpan={esGlobal ? 8 : 9} className="cdi-tabla-vacio">
                                        {esGlobal
                                                            ? condonaciones.length > 0
                                                                ? "No hay condonaciones que coincidan con el filtro."
                                                                : "No hay condonaciones registradas."
                                                            : "Este cliente no tiene condonaciones registradas."}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        {esGlobal && totalesGlobal && condonacionesFiltradas.length > 0 && (
                                            <tfoot>
                                                <tr className="cdi-tabla-totales">
                                                    <td className="cdi-tabla-nowrap" />
                                                    <td className="cdi-tabla-nowrap" />
                                                    <td className="cdi-tabla-nowrap">Total:</td>
                                                    <td className="cdi-tabla-nowrap cdi-tabla-pct">{FormatMoney(totalesGlobal.totalInteres)}</td>
                                                    <td className="cdi-tabla-nowrap cdi-tabla-pct">{FormatMoney(totalesGlobal.totalCondonado)}</td>
                                                    <td className="cdi-tabla-nowrap cdi-tabla-pct">
                                                        {(totalesGlobal.totalInteres ? (totalesGlobal.totalCondonado / totalesGlobal.totalInteres) * 100 : 0).toFixed(1)}%
                                                    </td>
                                                    <td className="cdi-tabla-nowrap cdi-tabla-pct">{FormatMoney(totalesGlobal.interesPagado)}</td>
                                                    <td className="cdi-tabla-nowrap" />
                                                    <td className="cdi-tabla-nowrap" />
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <CondonacionInteresDetalleModal
                open={Boolean(folioDetalle)}
                onClose={() => setFolioDetalle(null)}
                folio={folioDetalle}
            />
        </div>
    );
};

export default CondonacionInteresListado;
